import { apiClient } from './client';
import type { Invitation } from './invitations';

export interface SystemLog {
  id: string;
  created_at: string;
  pid: number;
  level: string;
  logger_name: string;
  user_id: string | null;
  /** Display name for user_id (full_name, else email); null for system
   *  logs with no user. Raw id kept for tooltips. */
  user_name: string | null;
  tenant_id: string | null;
  message: string;
  extra: Record<string, unknown> | null;
}

export interface SystemLogPage {
  items: SystemLog[];
  total: number;
  offset: number;
  limit: number;
}

export interface LogFilters {
  level?: string;
  user_id?: string;
  tenant_id?: string;
  since?: string; // ISO 8601
  search?: string;
  limit?: number;
  offset?: number;
}

export async function listLogs(filters: LogFilters = {}): Promise<SystemLogPage> {
  // Strip undefined keys — axios serializes them as `?key=undefined`
  // otherwise, which trips the server's strict query validation.
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) {
      params[k] = v;
    }
  }
  const { data } = await apiClient.get<SystemLogPage>('/api/v1/admin/logs', {
    params,
  });
  return data;
}

// ----------------------------------------------------------------------
// Audit trail
// ----------------------------------------------------------------------
export interface AuditEvent {
  id: string;
  created_at: string;
  actor_user_id: string | null;
  /** Display name for actor_user_id (full_name, else email); null for
   *  system-initiated events. Raw id kept for tooltips. */
  actor_name: string | null;
  tenant_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
}

export interface AuditEventPage {
  items: AuditEvent[];
  total: number;
  offset: number;
  limit: number;
}

export interface AuditFilters {
  actor_user_id?: string;
  tenant_id?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

export async function listAuditEvents(
  filters: AuditFilters = {},
): Promise<AuditEventPage> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) {
      params[k] = v;
    }
  }
  const { data } = await apiClient.get<AuditEventPage>(
    '/api/v1/admin/audit',
    { params },
  );
  return data;
}

/**
 * Tenant-scoped audit trail — the same governance view a tenant_admin gets
 * for their own tenant. The backend forces the tenant filter from the
 * authorized path id, so this can only ever return the caller's tenant.
 */
export async function listTenantAuditEvents(
  tenantId: string,
  filters: Pick<AuditFilters, 'action' | 'entity_type' | 'since' | 'limit' | 'offset'> = {},
): Promise<AuditEventPage> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) params[k] = v;
  }
  const { data } = await apiClient.get<AuditEventPage>(
    `/api/v1/tenants/${tenantId}/audit-events`,
    { params },
  );
  return data;
}

// ----------------------------------------------------------------------
// Mail settings
// ----------------------------------------------------------------------
export type MailProvider = 'smtp' | 'mailgun';

export interface MailSettings {
  mail_provider: MailProvider;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_use_tls: boolean;
  email_from: string;
  has_smtp_password: boolean;
  // Mailgun (HTTP API). The key itself is never returned.
  mailgun_domain: string;
  mailgun_base_url: string;
  has_mailgun_api_key: boolean;
  // HMAC signing key the /webhooks/mailgun endpoint verifies delivery /
  // bounce events against. Without it every inbound event is rejected, so
  // the delivery card can only show "sent". Never returned — presence only.
  has_mailgun_webhook_signing_key: boolean;
}

export async function getMailSettings(): Promise<MailSettings> {
  const { data } = await apiClient.get<MailSettings>('/api/v1/admin/mail/settings');
  return data;
}

// Partial-update body for the mail PATCH. See backend
// MailSettingsUpdate for the secret semantics (same for smtp_password
// and mailgun_api_key): empty string = keep existing,
// `clear_<field>: true` = forget the stored value, any non-empty value
// = store new value.
export interface MailSettingsUpdate {
  mail_provider?: MailProvider;
  smtp_host?: string;
  smtp_port?: number;
  smtp_user?: string;
  smtp_password?: string;
  smtp_use_tls?: boolean;
  email_from?: string;
  clear_smtp_password?: boolean;
  mailgun_api_key?: string;
  mailgun_domain?: string;
  mailgun_base_url?: string;
  clear_mailgun_api_key?: boolean;
  mailgun_webhook_signing_key?: string;
  clear_mailgun_webhook_signing_key?: boolean;
}

export async function updateMailSettings(
  body: MailSettingsUpdate,
): Promise<MailSettings> {
  const { data } = await apiClient.patch<MailSettings>(
    '/api/v1/admin/mail/settings',
    body,
  );
  return data;
}

export async function sendTestMail(to: string): Promise<void> {
  await apiClient.post('/api/v1/admin/mail/test', { to });
}

// ----------------------------------------------------------------------
// AI / Insights settings (ROADMAP TRACE-MARKER-LABELS, AI-COACH)
//
// One provider, OpenAI (OpenRouter was removed 2026-10). The key serves
// Whisper (transcription) and the chat model (AI coach, debrief points,
// badge reading). It is write-only — the response carries
// `has_openai_api_key`, never the secret. Both toggles default OFF until
// the platform-wide HIPAA posture is decided.
// ----------------------------------------------------------------------
export interface InsightsSettings {
  has_openai_api_key: boolean;
  /** Transcribe the anchor audio (stored as marker_labels_enabled). */
  marker_labels_enabled: boolean;
  coaching_enabled: boolean;
  /** The OpenAI chat model for the coach, debrief points and badges. */
  llm_model: string;
  /** $ per 1M tokens, from OpenAI's price list; null = cost unknown. */
  llm_price_input_per_mtok: number | null;
  llm_price_output_per_mtok: number | null;
}

export async function getInsightsSettings(): Promise<InsightsSettings> {
  const { data } = await apiClient.get<InsightsSettings>(
    '/api/v1/admin/insights/settings',
  );
  return data;
}

// Partial-update body for the insights PATCH. Same secret-field
// semantics as Mail Settings — empty-string key = "no change"; explicit
// `clear_openai_api_key: true` is the only path that wipes the stored
// value. A price sent as null clears it.
export interface InsightsSettingsUpdate {
  openai_api_key?: string;
  clear_openai_api_key?: boolean;
  marker_labels_enabled?: boolean;
  coaching_enabled?: boolean;
  llm_model?: string;
  llm_price_input_per_mtok?: number | null;
  llm_price_output_per_mtok?: number | null;
}

export async function updateInsightsSettings(
  body: InsightsSettingsUpdate,
): Promise<InsightsSettings> {
  const { data } = await apiClient.patch<InsightsSettings>(
    '/api/v1/admin/insights/settings',
    body,
  );
  return data;
}

/** The chat models the saved OpenAI key can use (model dropdown). */
export interface OpenAIModelsResponse {
  models: string[];
}

export async function getOpenAIModels(): Promise<OpenAIModelsResponse> {
  const { data } = await apiClient.get<OpenAIModelsResponse>(
    '/api/v1/admin/insights/openai-models',
  );
  return data;
}

// ----------------------------------------------------------------------
// Platform admins
// ----------------------------------------------------------------------
export interface PlatformAdmin {
  id: string;
  email: string;
  full_name: string | null;
  email_verified_at: string | null;
  created_at: string;
  // Mirror of the superadmin membership row's is_active flag — used
  // by the Switch in the Platform admins page. False = grant is
  // suspended; the user can still log in but has no superadmin role.
  is_active: boolean;
}

export interface PlatformInvitationOut {
  id: string;
  email: string;
  role: string;
  tenant_id: string | null;
  tenant_name: string | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export async function listPlatformAdmins(): Promise<PlatformAdmin[]> {
  const { data } = await apiClient.get<PlatformAdmin[]>(
    '/api/v1/admin/platform-admins',
  );
  return data;
}

// Read-only detail surface for the ViewPlatformAdmin page. Shape
// matches /users/me minus the memberships block (the only membership
// here would be superadmin/null, which the row already implies).
export interface PlatformAdminDetail {
  id: string;
  email: string;
  full_name: string | null;
  email_verified_at: string | null;
  created_at: string;
  phone: string | null;
  mobile: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  country: string | null;
}

export async function getPlatformAdmin(
  userId: string,
): Promise<PlatformAdminDetail> {
  const { data } = await apiClient.get<PlatformAdminDetail>(
    `/api/v1/admin/platform-admins/${userId}`,
  );
  return data;
}

export async function updatePlatformAdmin(
  userId: string,
  body: { is_active: boolean },
): Promise<PlatformAdmin> {
  const { data } = await apiClient.patch<PlatformAdmin>(
    `/api/v1/admin/platform-admins/${userId}`,
    body,
  );
  return data;
}

export async function invitePlatformAdmin(
  email: string,
): Promise<PlatformInvitationOut> {
  const { data } = await apiClient.post<PlatformInvitationOut>(
    '/api/v1/admin/invitations',
    { email },
  );
  return data;
}

// Pending platform-level (superadmin) invitations — the invited-but-
// not-joined half that the Platform Admins roster doesn't show. Shares
// the Invitation shape with the tenant-scoped flow so the same
// PendingInvitations panel renders both.
export async function listPlatformInvitations(): Promise<Invitation[]> {
  const { data } = await apiClient.get<Invitation[]>(
    '/api/v1/admin/invitations',
  );
  return data;
}

export async function revokePlatformInvitation(
  invitationId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/admin/invitations/${invitationId}`);
}

export interface PlatformMfaPolicy {
  totp_required: boolean;
}

export async function getPlatformMfaPolicy(): Promise<PlatformMfaPolicy> {
  const { data } = await apiClient.get<PlatformMfaPolicy>(
    "/api/v1/admin/mfa-policy",
  );
  return data;
}

export async function updatePlatformMfaPolicy(
  body: PlatformMfaPolicy,
): Promise<PlatformMfaPolicy> {
  const { data } = await apiClient.patch<PlatformMfaPolicy>(
    "/api/v1/admin/mfa-policy",
    body,
  );
  return data;
}

// --- Worker config (ROADMAP #43.5) ----------------------------------------

export interface WorkerConfig {
  /** Desired insights-worker replica count (1..16). Documentation
   *  only — the API doesn't drive the orchestrator. */
  desired_replica_count: number;
  /** Desired uvicorn process count inside the api pod (1..8). Read
   *  by the container entrypoint via the UVICORN_WORKERS env var;
   *  changing this requires a container recreate to take effect. */
  api_worker_processes: number;
}

export async function getWorkerConfig(): Promise<WorkerConfig> {
  const { data } = await apiClient.get<WorkerConfig>(
    "/api/v1/admin/worker-config",
  );
  return data;
}

export async function updateWorkerConfig(
  body: WorkerConfig,
): Promise<WorkerConfig> {
  const { data } = await apiClient.patch<WorkerConfig>(
    "/api/v1/admin/worker-config",
    body,
  );
  return data;
}

/** Delete the heartbeat row for an insights-worker replica. Useful
 *  for clearing ghost rows; an actually-alive worker re-registers on
 *  its next tick. Returns 204; throws on 404 (already cleared). */
export async function recycleWorker(instanceId: string): Promise<void> {
  await apiClient.post(
    `/api/v1/admin/workers/${encodeURIComponent(instanceId)}/recycle`,
  );
}

// ----------------------------------------------------------------------
// Platform-dashboard insights cards (DASH-PIPELINE-HEALTH, DASH-AI-SPEND)
// ----------------------------------------------------------------------

/** Counts across `insight_jobs.status`. `failed_recent` is the
 *  last-hour failure count — sized to mean "is the pipeline
 *  currently healthy", not "how reliable are we overall". */
export interface QueueDepth {
  pending: number;
  running: number;
  failed_recent: number;
  dead: number;
}

export interface WorkerHeartbeat {
  instance_id: string;
  last_seen_at: string;
  age_seconds: number;
  status: 'ok' | 'stale';
  version: string | null;
}

export interface PipelineStatus {
  queue: QueueDepth;
  oldest_pending_age_seconds: number | null;
  workers: WorkerHeartbeat[];
  checked_at: string;
}

export async function getPipelineStatus(): Promise<PipelineStatus> {
  const { data } = await apiClient.get<PipelineStatus>(
    '/api/v1/admin/insights/pipeline-status',
  );
  return data;
}

export type SpendRange = 'day' | 'week' | 'month';

export interface DailySpend {
  day: string;
  cost_usd: number;
  request_count: number;
}

export interface ProviderSpend {
  provider: string;
  cost_usd: number;
  request_count: number;
}

export interface ModelSpend {
  model_id: string;
  provider: string;
  cost_usd: number;
  request_count: number;
}

export interface AiSpendSummary {
  range: SpendRange;
  total_cost_usd: number;
  request_count: number;
  by_day: DailySpend[];
  by_provider: ProviderSpend[];
  by_model: ModelSpend[];
  checked_at: string;
}

export async function getAiSpend(
  range: SpendRange = 'month',
): Promise<AiSpendSummary> {
  const { data } = await apiClient.get<AiSpendSummary>(
    '/api/v1/admin/insights/ai-spend',
    { params: { range } },
  );
  return data;
}

// --- Email delivery (EMAIL-DELIVERY) ---------------------------------

export interface DailyEmailCount {
  day: string;
  sent: number;
  delivered: number;
  bounced: number;
  failed: number;
}

export interface DomainBounce {
  domain: string;
  bounced: number;
  sent: number;
}

export interface EmailDeliverySummary {
  range_days: number;
  total: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  by_day: DailyEmailCount[];
  top_bounced_domains: DomainBounce[];
  checked_at: string;
}

export async function getEmailDelivery(
  days = 30,
): Promise<EmailDeliverySummary> {
  const { data } = await apiClient.get<EmailDeliverySummary>(
    '/api/v1/admin/email-delivery',
    { params: { days } },
  );
  return data;
}

/** Same shape as getEmailDelivery, but scoped to one tenant's outbound mail
 *  (tenant-admin dashboard card). Only tenant-attributed sends — invitations,
 *  participant welcomes, session-ready notices — are counted. */
export async function getTenantEmailDelivery(
  tenantId: string,
  days = 30,
): Promise<EmailDeliverySummary> {
  const { data } = await apiClient.get<EmailDeliverySummary>(
    `/api/v1/tenants/${tenantId}/email-delivery`,
    { params: { days } },
  );
  return data;
}

// --- Access requests (pending users) ---------------------------------

export interface AccessRequestUser {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  /**
   * Where the signup came from, from this user's `user.registered` audit row.
   * Null for accounts that registered before the audit trail captured an IP.
   * Behind a reverse proxy it is only the true client address when the API
   * runs with proxy headers trusted (staging and up).
   */
  registered_ip: string | null;
}

/** Pending users — verified but holding no active membership. */
export async function listAccessRequests(): Promise<AccessRequestUser[]> {
  const { data } = await apiClient.get<AccessRequestUser[]>(
    '/api/v1/admin/access-requests',
  );
  return data;
}

/**
 * Grant a role to a pending user. `tenantId` is required for
 * tenant_admin / participant and must be null for superadmin.
 */
export async function grantAccess(
  userId: string,
  role: 'superadmin' | 'tenant_admin' | 'participant',
  tenantId: string | null,
): Promise<void> {
  await apiClient.post(`/api/v1/admin/access-requests/${userId}/grant`, {
    role,
    tenant_id: tenantId,
  });
}

/**
 * Dismiss (delete) a pending self-registered account that holds no role.
 * Clears junk / abandoned signups from the list. Rejected (409) by the
 * server if the user actually holds an active membership.
 */
export async function dismissAccessRequest(userId: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/access-requests/${userId}`);
}

// ---------------------------------------------------------------------
// Cross-tenant physical devices (PHYSICAL-DEVICE-IDENTITY)
// ---------------------------------------------------------------------

/** One tenant a physical tablet is enrolled in (a per-tenant device row). */
export interface PhysicalDeviceEnrollment {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  device_id: string;
  label: string;
  status: 'active' | 'suspended' | 'lost' | 'retired';
  last_seen_at: string | null;
}

/**
 * A physical tablet keyed on its stable OS device id, plus every tenant it's
 * enrolled in. `app_version` is authoritative (one per physical device) — the
 * answer to "same device, different version per tenant".
 */
export interface PhysicalDevice {
  hardware_id: string;
  app_version: string | null;
  make: string | null;
  model: string | null;
  os_name: string | null;
  os_version: string | null;
  first_seen_at: string;
  last_seen_at: string;
  enrollments: PhysicalDeviceEnrollment[];
}

export async function listPhysicalDevices(): Promise<PhysicalDevice[]> {
  const { data } = await apiClient.get<PhysicalDevice[]>(
    '/api/v1/admin/physical-devices',
  );
  return data;
}

// ---------------------------------------------------------------------
// Scoring throughput (JOB-TIMINGS)
// ---------------------------------------------------------------------

/** One completed job: how long it took, against how much data it read. */
export interface JobTimingPoint {
  kind: string;
  seconds: number;
  /** Records in the recording; null for ones scored before sample counting. */
  records: number | null;
  recording_seconds: number | null;
  finished_at: string;
}

export interface JobKindTiming {
  kind: string;
  count: number;
  median_seconds: number;
  p95_seconds: number;
  max_seconds: number;
  /** Median seconds per 100k records. Null for kinds with no size relation. */
  seconds_per_100k_records: number | null;
}

export interface JobTimings {
  days: number;
  kinds: JobKindTiming[];
  points: JobTimingPoint[];
}

export async function getJobTimings(days = 7): Promise<JobTimings> {
  const { data } = await apiClient.get<JobTimings>(
    `/api/v1/admin/insights/job-timings?days=${days}`,
  );
  return data;
}

// ---------------------------------------------------------------------
// Data integrity — where recordings have gone missing, and what can be
// done about each kind. See api/app/services/data_integrity.py.
// ---------------------------------------------------------------------

export interface StrandedSession {
  /** capture_sessions row UUID — the key the disable endpoint takes.
   *  `session_id` is the capture-app's own id, which re-score takes.
   *  Different keys; using the wrong one 404s. */
  row_id: string;
  tenant_slug: string;
  tenant_id: string;
  session_id: string;
  participant_label: string | null;
  device_label: string | null;
  started_at: string;
  upload_state: string;
  expected_samples: number | null;
  age_hours: number;
  /** False once old enough that whether the device still holds the file
   *  depends on upgrade/reinstall history the server cannot see. */
  actionable: boolean;
}

export interface StaleDevice {
  tenant_slug: string;
  label: string | null;
  app_version: string | null;
  last_seen_at: string | null;
  pending_sessions: number;
}

export interface DataIntegrity {
  generated_at: string;
  tenants_scanned: number;
  landed_unscored: StrandedSession[];
  never_uploaded_recent: StrandedSession[];
  never_uploaded_aged: StrandedSession[];
  empty_recordings: StrandedSession[];
  stale_devices: StaleDevice[];
  recoverable: number;
}

export interface RescoreResult {
  session_id: string;
  enqueued: string[];
  already_inflight: string[];
}

export async function getDataIntegrity(): Promise<DataIntegrity> {
  const { data } = await apiClient.get<DataIntegrity>(
    '/api/v1/admin/insights/data-integrity',
  );
  return data;
}

/**
 * The same report, scanned for ONE tenant and readable by that tenant's own
 * admins and coordinators.
 *
 * A separate endpoint rather than the platform one with a filter: the
 * platform scan is cross-tenant by default, so the tenant boundary is
 * enforced server-side from the authorised tenant row — never from anything
 * sent by the caller.
 */
export async function getTenantDataIntegrity(
  tenantId: string,
): Promise<DataIntegrity> {
  const { data } = await apiClient.get<DataIntegrity>(
    `/api/v1/tenants/${tenantId}/data-integrity`,
  );
  return data;
}

export async function rescoreSession(
  tenantId: string,
  sessionId: string,
): Promise<RescoreResult> {
  const { data } = await apiClient.post<RescoreResult>(
    '/api/v1/admin/insights/rescore',
    { tenant_id: tenantId, session_id: sessionId },
  );
  return data;
}
